import { addBusinessDays } from "@redotech/redo-common-model/date/business-day";
import { allowedProblemPickPolicyIdFormat } from "@redotech/redo-fulfillment-id";
import * as z from "zod";

/**
 * shipmentEligibility controls which groups are eligible for allowed problem picks.
 * productEligibility controls which products on eligible groups are allowed to be
 * picked without inventory.
 *
 * If we ever need more complex logic than this, we should use filterGroups as the policy
 * and write a more complex evaluator
 */
export const AllowedProblemPickPolicySchema = z.object({
  shipmentEligibility: z.object({
    // Minimum order age in BUSINESS days (weekends + US holidays skipped).
    minOrderAgeDays: z.number().nullish(),
    shipmentTags: z.array(z.string()).nullish(),
  }),
  productEligibility: z.object({ vendors: z.array(z.string()).nullish() }),
});

export type AllowedProblemPickPolicy = z.infer<
  typeof AllowedProblemPickPolicySchema
>;

/**
 * A stored, named policy. `id` is the CRDB row id (prefixed string), absent for
 * a policy added in the UI but not yet saved.
 */
export const NamedAllowedProblemPickPolicySchema = z.object({
  id: allowedProblemPickPolicyIdFormat.schema,
  name: z.string(),
  policy: AllowedProblemPickPolicySchema,
});

export type NamedAllowedProblemPickPolicy = z.infer<
  typeof NamedAllowedProblemPickPolicySchema
>;

/**
 * Computes the instant at which a line item becomes eligible to be picked
 * without inventory under the policy, or null if it never is.
 *
 * Returning an absolute timestamp lets the index compare it against `now` at
 * query/display time, so eligibility flips as time passes with no reindex.
 *
 * Both tiers must pass:
 *  - productEligibility: a narrowing filter — an empty vendor list allows all
 *    products, a non-empty list requires the product's vendor to be on it.
 *  - shipmentEligibility: REQUIRED. OR'd — eligible immediately on an allowed
 *    tag, otherwise once the order reaches the minimum age in business days
 *    (weekends + US holidays skipped). With no shipment condition at all the
 *    policy matches nothing, so it can never become a catch-all.
 */
export function computeAllowedProblemPickEligibleAt(
  policy: AllowedProblemPickPolicy,
  context: {
    shipment: { orderDate: Date; tags: string[] };
    product: { vendor: string | null };
  },
): Date | null {
  const { shipment, product } = context;

  const vendors = policy.productEligibility.vendors ?? [];
  // Empty vendor list is a no-op narrowing filter: all products qualify. A
  // non-empty list is an allow-list — the product's vendor must be on it.
  const productEligible =
    vendors.length === 0 ||
    (product.vendor != null && vendors.includes(product.vendor));
  if (!productEligible) {
    return null;
  }

  const shipmentTags = policy.shipmentEligibility.shipmentTags ?? [];
  const minOrderAgeDays = policy.shipmentEligibility.minOrderAgeDays;

  // A policy must narrow by shipment. With no shipment condition it matches
  // nothing — a fully-open policy would allow problem picks for everything.
  if (shipmentTags.length === 0 && minOrderAgeDays == null) {
    return null;
  }

  // Conditions are OR'd; return the earliest instant the shipment qualifies.
  // Tags are compared case-insensitively: the settings UI lowercases tag
  // selections, while a fulfillment group's tags retain their original case.
  const lowerShipmentTags = new Set(
    shipmentTags.map((tag) => tag.toLowerCase()),
  );
  const tagEligible =
    lowerShipmentTags.size > 0 &&
    shipment.tags.some((tag) => lowerShipmentTags.has(tag.toLowerCase()));
  if (tagEligible) {
    return shipment.orderDate;
  }

  if (minOrderAgeDays != null) {
    // Order age is measured in business days, using the shared business-day
    // helpers (weekends + US holidays skipped). Counted in UTC so the index and
    // pick-wave agree on the eligibility instant.
    const orderZdt = Temporal.Instant.fromEpochMilliseconds(
      shipment.orderDate.getTime(),
    ).toZonedDateTimeISO("UTC");
    const eligibleZdt = addBusinessDays({
      date: orderZdt,
      businessDays: minOrderAgeDays,
      businessEntity: undefined,
    });
    return new Date(eligibleZdt.toInstant().epochMilliseconds);
  }

  return null;
}

/** Policy short-picks only multi-item groups; single-item picks reject items without inventory. */
export function policyAppliesToGroup(groupItemQuantity: number): boolean {
  return groupItemQuantity > 1;
}

/**
 * Eligibility across several independent policies: a line item is eligible if
 * ANY policy allows it, at the earliest instant any of them does.
 */
export function computeAllowedProblemPickEligibleAtForPolicies(
  policies: readonly AllowedProblemPickPolicy[],
  context: {
    shipment: { orderDate: Date; tags: string[] };
    product: { vendor: string | null };
  },
): Date | null {
  let earliest: Date | null = null;
  for (const policy of policies) {
    const eligibleAt = computeAllowedProblemPickEligibleAt(policy, context);
    if (eligibleAt && (earliest === null || eligibleAt < earliest)) {
      earliest = eligibleAt;
    }
  }
  return earliest;
}
